/* tslint:disable:forbid-import */
// Because this decorator applies hotkeys outside of render, we need to
// manually hook into mobx to support hot-swapping of keysets.
import { autorun } from 'mobx';
/* tslint:enable:forbid-import */
import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { HotkeyCommand, Hotkeys } from '../types/HotkeyCommand';
import fixUpHotkeyCommand from 'owa-hotkeys/lib/utils/fixupHotkeyCommand';
import { lazyMousetrapImport } from 'owa-hotkeys/lib/utils/lazyMousetrapImport';
import getKeysForKeyboardShortcutsMode from 'owa-hotkeys/lib/utils/getKeysForKeyboardShortcutsMode';
import shouldPreventCallback from 'owa-hotkeys/lib/utils/shouldPreventCallback';

/** Key for map from command -> unbound handler */
const HANDLER_MAP_KEY = '__keydownHandlerMap';
const KEYMAPHANDLER_HOOKS_ADDED = '__keydownHandlerHooksAdded';

export type Hotkeys = string | string[];

export type HotkeyCommand = {
    hotmail?: Hotkeys;
    yahoo?: Hotkeys;
    gmail?: Hotkeys;
    owa?: Hotkeys;
};

export interface KeydownHandlerModifiedClass extends React.Component<any, any, any> {
    __mousetrap: any;
    __cleanupMousetrap: Promise<Function>;
}

function updateHotkeys(
    this: KeydownHandlerModifiedClass,
    target: React.ComponentLifecycle<any, any, any>
) {
    // Clean up the previous keybindings, in the event this is an autorun due to keyset swapping
    this.__mousetrap.reset();

    let handlerMap = target[HANDLER_MAP_KEY];
    let handlerNames = Object.keys(handlerMap);
    for (let i = 0; i < handlerNames.length; i++) {
        let handlerMethodName = handlerNames[i];
        let keys = getKeysForKeyboardShortcutsMode(handlerMap[handlerMethodName].command);
        let handlerMethod = (evt: KeyboardEvent) => {
            if (shouldPreventCallback(evt, handlerMap[handlerMethodName].allowHotkeyOnTextFields)) {
                return;
            }
            if (handlerMap[handlerMethodName].stopPropagation) {
                evt.stopPropagation();
            }
            if (handlerMap[handlerMethodName].preventDefault) {
                evt.preventDefault();
            }
            target[handlerMethodName].call(this, evt);
        };

        if (keys) {
            this.__mousetrap.bind(keys, handlerMethod);
        }
    }
}

export function keydownHandler(
    command: HotkeyCommand | Hotkeys,
    stopPropagation: boolean = true,
    preventDefault: boolean = true,
    allowHotkeyOnTextFields: boolean = false
) {
    return function(
        target: React.Component<any, any, any>,
        methodName: string,
        descriptor: PropertyDescriptor
    ) {
        if (typeof window !== 'object') {
            return;
        }

        // Store the command in the map of handlers this class prototype contains.
        target[HANDLER_MAP_KEY] = target[HANDLER_MAP_KEY] || {};
        target[HANDLER_MAP_KEY][methodName] = {
            command: fixUpHotkeyCommand(command),
            stopPropagation: stopPropagation,
            preventDefault: preventDefault,
            allowHotkeyOnTextFields: allowHotkeyOnTextFields,
        };

        // If a previous keydownHandler decorator has not already added the necessary hooks
        // to the prototype, do so now.
        if (!target[KEYMAPHANDLER_HOOKS_ADDED]) {
            var onMount = target.componentDidMount;
            target.componentDidMount = function(this: KeydownHandlerModifiedClass) {
                if (onMount) {
                    onMount.call(this);
                }

                const MousetrapImportPromise = lazyMousetrapImport.import();
                let disposeMousetrapAutorun: Function;

                const cleanupCallback = () => {
                    // Clean up the keybindings
                    if (this.__mousetrap) {
                        this.__mousetrap.reset();
                    }

                    // Dispose the mobx autorun
                    if (disposeMousetrapAutorun) {
                        disposeMousetrapAutorun();
                    }
                };

                const setupPromise = MousetrapImportPromise.then(Mousetrap => {
                    this.__mousetrap = new Mousetrap(ReactDOM.findDOMNode(this) as (
                        | Element
                        | undefined));

                    // Because this is not part of render, we need to manually hook into mobx for hot-swapping of keysets
                    disposeMousetrapAutorun = autorun(updateHotkeys.bind(this, target));
                }).catch(cleanupCallback);

                this.__cleanupMousetrap = setupPromise.then(() => cleanupCallback);
            };

            var onUnmount = target.componentWillUnmount;
            target.componentWillUnmount = function(this: KeydownHandlerModifiedClass) {
                if (onUnmount) {
                    onUnmount.call(this);
                }

                if (this.__cleanupMousetrap) {
                    this.__cleanupMousetrap.then(fn => fn());
                }
            };

            // Set the flag to ensure the hooks are not duplicated on this prototype.
            target[KEYMAPHANDLER_HOOKS_ADDED] = true;
        }
    };
}

export interface UseKeydownHandlerOptions {
    stopPropagation?: boolean;
    preventDefault?: boolean;
    allowHotkeyOnTextFields?: boolean;
}

export function useKeydownHandler<TElement extends HTMLElement>(
    ref: React.MutableRefObject<TElement | null>,
    command: HotkeyCommand | Hotkeys,
    callback: (evt: KeyboardEvent) => void,
    {
        stopPropagation = true,
        preventDefault = true,
        allowHotkeyOnTextFields = false,
    }: UseKeydownHandlerOptions = {}
) {
    const handlerMethod = React.useCallback(
        (evt: KeyboardEvent) => {
            if (shouldPreventCallback(evt, allowHotkeyOnTextFields)) {
                return;
            }
            if (stopPropagation) {
                evt.stopPropagation();
            }
            if (preventDefault) {
                evt.preventDefault();
            }
            callback(evt);
        },
        [callback, stopPropagation, preventDefault, allowHotkeyOnTextFields]
    );

    const keys = React.useMemo(() => getKeysForKeyboardShortcutsMode(command), [command]);
    const Mousetrap = lazyMousetrapImport.tryImportForRender();

    React.useEffect((): (() => void) | void => {
        if (!Mousetrap || !keys || !ref.current) {
            return;
        }

        const mousetrapElementBinding = new Mousetrap(ref.current);
        mousetrapElementBinding.bind(keys, handlerMethod);

        return () => mousetrapElementBinding.reset();
    }, [Mousetrap, keys, ref, handlerMethod]);
}
